import React, { useState, useEffect } from "react";
import Breadcrumb from "../../../components/Breadcrumb";
import Grid from "../../../components/Grid";
import LiveSessionsService from "../../../Api/LiveSessionsService";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2/dist/sweetalert2.js';

const columns = [
    { 
      header: 'Title',
      key: 'title',
      render: (item) => (
        <div className="d-flex align-items-center">
          <div className="flex-grow-1">
            <span className="text-md mb-0 fw-normal text-secondary-light">
              {item.title}
            </span>
          </div>
        </div>
      )
    },
    {
      header: 'Course',
      key: 'Course',
      render: (item) => (
        <span>{item.Course?.title || 'N/A'}</span>
      )
    },
    {
      header: 'Session Date',
      key: 'session_datetime',
      render: (item) => (
        <span>{new Date(item.session_datetime).toLocaleString()}</span>
      )
    },
    {
      header: 'Duration (minutes)',
      key: 'session_duration_minutes'
    },
    {
      header: 'Status',
      key: 'session_url',
      center: true,
      render: (item) => (
        <span className={`${item.session_url ? 'bg-success-focus text-success-600 border border-success-main' : 'bg-neutral-200 text-neutral-600 border border-neutral-400'} px-24 py-4 radius-4 fw-medium text-sm`}>
          {item.session_url ? 'Active' : 'Pending'}
        </span>
      )
    }
];

const LiveSessionsListPage = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const sessionsAPI = new LiveSessionsService();

  // Add state for search and filter
  const [searchTitle, setSearchTitle] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchSessions = async (title = "", status = "") => {
    try {
      setLoading(true);
      const response = await sessionsAPI.GetLiveSessions();
      if (response.data && response.data.data) {
        // Filter out cancelled sessions (show only is_cancelled = false)
        const nonCancelledSessions = response.data.data.filter(session => !session.is_cancelled);
        setSessions(nonCancelledSessions);
        toast.success('Live sessions loaded successfully!');
      }
    } catch (err) {
      console.error("Error fetching live sessions:", err);
      toast.error("Failed to load live sessions. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Handle search change
  const handleSearch = (searchTerm) => {
    setSearchTitle(searchTerm);
    setCurrentPage(1);
    fetchSessions(searchTerm, statusFilter);
  };

  // Handle status filter change
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
    fetchSessions(searchTitle, status);
  };

  // Handle items per page change
  const handleItemsPerPage = (perPage) => {
    setItemsPerPage(perPage);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleView = (session) => {
    navigate(`/view-live-session?id=${session.id}`);
  };

  const handleDelete = async (session) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, cancel it!',
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          try {
            const response = await sessionsAPI.CancelLiveSession(session.id);
            if (response.data && response.data.status === "success") {
              return true;
            } else {
              throw new Error(response.data?.message || 'Failed to cancel live session');
            }
          } catch (error) {
            Swal.showValidationMessage(
              `Cancel failed: ${error.response?.data?.message || error.message}`
            );
          }
        },
        allowOutsideClick: () => !Swal.isLoading()
      });

      if (result.isConfirmed) {
        await Swal.fire({
          title: 'Cancelled!',
          text: 'Live session has been cancelled.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
        toast.success('Live session cancelled successfully!');
        fetchSessions(searchTitle, statusFilter);
      }
    } catch (error) {
      console.error("Error in delete operation:", error);
      toast.error('An unexpected error occurred');
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb title="Live Sessions List" />
      
      <Grid 
          data={sessions}
          columns={columns}
          add="add-live-session"
          onDelete={handleDelete}
          onView={handleView}
          onSearch={handleSearch}
          onStatusFilter={handleStatusFilter}
          onItemsPerPageChange={handleItemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
        />
    </>
  );
};

export default LiveSessionsListPage;
